<template>
  <v-app id="app_user_list">

    <v-overlay v-model="centerLoading" :scrim="false" class="centered-overlay">
      <v-progress-circular color="#800080" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-container fluid>
      <v-row class="pl-2 pr-6">
        <v-col class="mt-3" cols="7">
          <p class="custom_page_main_title">REGISTERED USER LIST</p>
          <p class="custom_page_main_sub_title" style="opacity: 0.6">Analyse already registered user data.</p>
        </v-col>
        <v-col class="px-4 mt-5" cols="5">
          <v-row class="custom_tool_bar_style">
            <v-btn color="#800080" size="x-large" variant="text" @click="createUser()">
              <v-icon>mdi-plus-circle</v-icon>&nbsp;&nbsp;CREATE USER
            </v-btn>
            <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
            <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
              <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <!-- STAT CARDS ROW -->
      <v-row class="mt-4 px-2">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card total-users" elevation="2" rounded="lg">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label">Total Users</div>
                  <div class="stat-number">{{ totalUsers }}</div>
                  <div class="stat-progress mt-2">
                    <v-progress-linear 
                      :model-value="(totalUsers / maxUsers) * 100" 
                      color="#800080" 
                      height="6"
                      rounded
                    ></v-progress-linear>
                    <div class="stat-subtext">{{ totalUsers }} / {{ maxUsers }} users</div>
                  </div>
                </div>
                <v-icon class="stat-icon" size="36" color="#800080">mdi-account-group</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card web-users" elevation="2" rounded="lg">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label">Web Users</div>
                  <div class="stat-number">{{ webUsers }}</div>
                  <div class="stat-progress mt-2">
                    <v-progress-linear
                      :model-value="(webUsers / maxWebUsers) * 100"
                      color="#1976D2"
                      height="6"
                      rounded
                    ></v-progress-linear>
                    <div class="stat-subtext">{{ webUsers }} / {{ maxWebUsers }} users</div>
                  </div>
                </div>
                <v-icon class="stat-icon" size="36" color="#1976D2">mdi-laptop</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card mobile-users" elevation="2" rounded="lg">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label">Mobile Users</div>
                  <div class="stat-number">{{ mobileUsers }}</div>
                  <div class="stat-progress mt-2">
                    <v-progress-linear
                      :model-value="(mobileUsers / maxMobileUsers) * 100"
                      color="#2E7D32"
                      height="6"
                      rounded
                    ></v-progress-linear>
                    <div class="stat-subtext">{{ mobileUsers }} / {{ maxMobileUsers }} users</div>
                  </div>
                </div>
                <v-icon class="stat-icon" size="36" color="#2E7D32">mdi-cellphone</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card both-users" elevation="2" rounded="lg">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label">Web & Mobile Users</div>
                  <div class="stat-number">{{ bothUsers }}</div>
                  <div class="stat-percentage mt-1">
                    <span class="green--text">{{ totalUsers > 0 ? Math.round((bothUsers / totalUsers) * 100) : 0 }}%</span>
                    <span class="text-caption grey--text"> of total</span>
                  </div>
                </div>
                <v-icon class="stat-icon" size="36" color="#E65100">mdi-sync</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="custom_card_box mt-4" elevation="1" border="sm opacity-1" rounded="lg">
        <v-card-text class="custom_tool_bar_style">
          <v-btn class="custom_pdf_btn" variant="outlined" @click="exportPDF">
            <v-icon size="large">mdi-file-pdf</v-icon>&nbsp;&nbsp;PDF
          </v-btn>
          <v-btn class="custom_excel_btn ml-4" variant="outlined" @click="exportExcel">
            <v-icon size="large">mdi-file-excel</v-icon>&nbsp;&nbsp;Excel
          </v-btn>
        </v-card-text>

        <v-divider thickness="2"></v-divider>

        <v-card-text>
          <v-row class="mt-0">
            <v-col cols="12" md="4">
              <v-autocomplete v-model="selectedUserStatus" :items="userStatusItems" clearable 
                              label="User Status" multiple placeholder="User Status" 
                              variant="outlined" density="comfortable" class="custom_input_field_color">
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete v-model="selectedUserTypes" :items="userTypes" clearable item-title="role_type"
                              item-value="role_type" label="User Role Type" multiple 
                              placeholder="User Type" variant="outlined" density="comfortable"
                              class="custom_input_field_color">
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete v-model="selectedCompanies" :items="allCompanies" clearable 
                              label="Piyestra Company" multiple placeholder="Select Company" 
                              variant="outlined" density="comfortable" class="custom_input_field_color">
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row class="mt-0">
            <v-col cols="12" md="4">
              <v-autocomplete v-model="selectedUserCategories" :items="userCategoryItems" clearable 
                              label="User Category" multiple placeholder="User Category" 
                              variant="outlined" density="comfortable" class="custom_input_field_color">
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete v-model="selectedRegionalCodes" :items="allRegionalCodes" clearable 
                              label="Regional Code" multiple placeholder="Select Regional Code" 
                              variant="outlined" density="comfortable" class="custom_input_field_color">
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field v-model="search" append-inner-icon="mdi-magnify" clearable 
                           hide-details label="Search" placeholder="Search" variant="outlined" 
                           density="comfortable" class="custom_input_field_color">
              </v-text-field>
            </v-col>
          </v-row>

          <v-divider></v-divider>

          <v-data-table v-model:page="page" :loading="loading" :headers="headers" :items="filteredItems"
                        :items-per-page="itemsPerPage" class="custom_table_normal" 
                        @page-count="totalPages = $event">

            <template v-slot:[`item.user_name`]="{ item }">
              <div>
                <strong>{{ item.user_name }}</strong>
                <br/>
                <v-chip
                    :color="item.status === 'Activate' ? 'green' : item.status === 'Deactivate' ? 'red' : 'gray'"
                    size="small" class="mt-1 mb-1">
                  <strong>
                    {{ item.status === 'Activate' ? 'ACTIVE ✅' : 'INACTIVE ❌' }}
                  </strong>
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.mobile_numbers`]="{ item }">
              <div>
                <v-chip v-for="(mobile, index) in item.mobile_numbers" :key="index" 
                        size="small" class="px-1 ma-1" label variant="text">
                  {{ mobile.country_code }} {{ mobile.mobile_number }}
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.piyestra_companies`]="{ item }">
              <div>
                <v-chip v-for="(company, index) in item.piyestra_companies" :key="index" 
                        size="small" class="px-1 ma-1" color="#800080" variant="outlined">
                  {{ company }}
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.regional_codes`]="{ item }">
              <div>
                <v-chip v-for="(code, index) in item.regional_codes" :key="index" 
                        size="small" class="px-1 ma-1" color="#800080" variant="outlined">
                  {{ code }}
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.area_codes`]="{ item }">
              <div>
                <v-chip v-for="(code, index) in item.area_codes" :key="index" 
                        size="small" class="px-1 ma-1" color="#800080" variant="outlined">
                  {{ code }}
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.action`]="{ item }">
              <v-menu transition="slide-x-transition" id="app_user_list">
                <template v-slot:activator="{ props }">
                  <v-btn variant="flat" size="large" style="background-color: #800080; color: white;" 
                         v-bind="props">
                    ACTIONS &nbsp; &nbsp;<v-icon>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-if="permissions.read" @click="viewItem(item)">
                    <v-list-item-title class="custom_font_color_view">VIEW</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item v-if="permissions.write" @click="editItem(item)">
                    <v-list-item-title class="custom_font_color_edit">EDIT</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item v-if="permissions.delete" @click="confirmDelete(item)">
                    <v-list-item-title class="custom_font_color_delete">DELETE</v-list-item-title>
                  </v-list-item>

                  <v-list-item v-if="permissions.write" @click="confirmStatusChange(item)">
                    <v-list-item-title class="custom_font_color_activate_user">
                      {{ item.status === 'Activate' ? 'DEACTIVATE USER' : 'ACTIVATE USER' }}
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item v-if="permissions.write" @click="lockItem(item)">
                    <v-list-item-title class="custom_font_color_password_change">CHANGE PASSWORD</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- View User Dialog - FIXED SCROLL -->
      <v-dialog v-model="view_user_dialog" fullscreen id="app_user_list">
        <v-card flat class="d-flex flex-column" style="height: 100vh; overflow: hidden;">
          <v-toolbar style="border-radius: 0px !important; flex-shrink: 0;"
                     class="custom_fullscreen_dialog_toolbar pr-5 custom_card_box" color="white" dense>
            <v-toolbar-title>
              <v-img class="custom_company_logo_nav" :src="require('@/img/company_logo/logo-dark 2.png')"
                     @click="homeReload()"></v-img>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn style="background-color: #800080; color: white;" variant="flat" @click="view_user_dialog = false">
              CLOSE
            </v-btn>
          </v-toolbar>

          <v-container fluid class="pa-0 flex-grow-1" style="overflow-y: auto; height: 0; min-height: 0;">
            <v-row class="ma-0" style="padding: 16px 0;">
              <v-col cols="12" md="12">
                <v-card class="pa-0 mx-6 mt-2 custom_card_box" border="sm opacity-1">
                  <template v-slot:prepend>
                    <v-icon>mdi-view-dashboard</v-icon>
                    <v-card-title class="font-weight-bold custom_page_main_sub_title ml-3">USER DETAILS</v-card-title>
                  </template>
                </v-card>

                <v-card class="pa-3 mx-6 custom_card_box mt-4 mb-4" border="sm opacity-1">
                  <v-row class="ma-0">
                    <v-col cols="12" xs="12" sm="9" md="9" lg="9">
                      <v-row>
                        <v-col cols="12" xs="12" sm="3" md="2" lg="2">
                          <v-img 
                            :src="brokenImages.has(user.id) ? defaultAvatar : getUserImageUrl(user)" 
                            aspect-ratio="1" 
                            class="user_image_view" 
                            contain
                            @error="handleImageError(user.id)"
                          >
                            <template v-slot:placeholder>
                              <v-row class="fill-height ma-0" align="center" justify="center">
                                <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                              </v-row>
                            </template>
                          </v-img>
                        </v-col>
                        <v-col cols="12" xs="12" sm="9" md="10" lg="10">
                          <div class="d-flex">
                            <p class="font-weight-bold custom_page_component_main_title text-body-1">Full name: &nbsp;</p>
                            <p class="font-weight-bold custom_page_component_main_title"
                               style="color: #05146F !important;">{{ user.name }}</p>
                          </div>

                          <div class="d-flex mt-1">
                            <p class="font-weight-bold custom_page_component_main_title text-body-1">User Code: &nbsp;</p>
                            <p class="font-weight-medium text-body-2 custom_page_component_main_title mt-1">
                              {{ user.user_code }}
                            </p>
                          </div>

                          <div class="d-flex mt-1">
                            <p class="font-weight-bold custom_page_component_main_title text-body-1">Mobile: &nbsp;</p>
                            <p class="font-weight-medium text-body-2 custom_page_component_main_title mt-1">
                              {{ user.mobile_numbers ? user.mobile_numbers.map(num => num.country_code + num.mobile_number).join(', ') : '' }}
                            </p>
                          </div>

                          <div class="d-flex align-center mt-6" style="gap: 12px; flex-wrap: wrap;">
                            <v-chip class="text-body-1 custom_green_chip" variant="outlined">
                              <v-icon color="#1B5E20" size="small" class="mr-1">mdi-check-circle-outline</v-icon>
                              {{ user.user_category }} - &nbsp;<span style="text-transform: uppercase !important;">{{ user.user_type }}</span>
                            </v-chip>
                            
                            <v-chip :color="user.status === 'Activate' ? 'green' : 'red'" variant="outlined">
                              <v-icon :color="user.status === 'Activate' ? 'green' : 'red'" size="small" class="mr-1">
                                {{ user.status === 'Activate' ? 'mdi-check-circle' : 'mdi-close-circle' }}
                              </v-icon>
                              {{ user.status === 'Activate' ? 'ACTIVE' : 'INACTIVE' }}
                            </v-chip>
                            
                            <v-chip v-if="isUserSalesRepOrAreaManager(user.user_type)" 
                                    :color="user.geofence_enabled === 1 ? '#800080' : '#9E9E9E'" 
                                    variant="flat"
                                    text-color="white"
                                    class="font-weight-medium">
                              <v-icon :color="'white'" size="small" class="mr-1">
                                {{ user.geofence_enabled === 1 ? 'mdi-check-circle' : 'mdi-close-circle' }}
                              </v-icon>
                              {{ user.geofence_enabled === 1 ? 'GEOFENCE ON' : 'GEOFENCE OFF' }}
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>

                    <v-col cols="12" xs="12" sm="3" md="3" lg="3">
                      <v-row class="text-right mt-3 mr-3">
                        <v-spacer></v-spacer>
                        <v-btn v-if="permissions.write" class="mr-3 custom_edit_btn" icon rounded 
                               size="small" variant="outlined" @click="editItem(user)">
                          <v-icon>mdi-pencil</v-icon>
                          <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                        </v-btn>

                        <v-btn v-if="permissions.delete" class="custom_delete_btn" icon rounded
                               size="small" variant="outlined" @click="confirmDelete(user)">
                          <v-icon>mdi-trash-can</v-icon>
                          <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                        </v-btn>
                      </v-row>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="12" lg="12">
                      <v-divider></v-divider>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" xs="12" sm="12" md="6" lg="6">
                      <v-text-field v-model="user.name" label="Full Name" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>

                      <v-text-field v-model="user.user_name" label="User Name" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>

                      <v-text-field v-model="user.user_code" label="User Code" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>

                      <v-text-field v-model="user.user_type" label="User Role Type" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>

                      <v-text-field v-model="user.user_category" label="User Category" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>

                      <v-text-field v-model="user.email" label="Email Address" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>

                      <v-text-field v-model="user.nic" label="NIC" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="6" lg="6">
                      <v-text-field v-model="user.registration_num" label="Registration Number" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>

                      <v-text-field v-model="user.gender" label="Gender" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>

                      <v-text-field v-model="user.address" label="Address" readonly
                                    variant="outlined" density="comfortable"
                                    class="px-3 custom_input_field_class_view custom_input_field_color">
                      </v-text-field>

                      <v-card class="pa-3 mt-2" variant="outlined">
                        <p class="font-weight-bold">Piyestra Companies:</p>
                        <v-chip v-for="(company, index) in user.piyestra_companies" :key="index" 
                                class="ma-1" color="#800080" variant="outlined">
                          {{ company }}
                        </v-chip>
                      </v-card>

                      <v-card class="pa-3 mt-2" variant="outlined">
                        <p class="font-weight-bold">Regional Codes:</p>
                        <v-chip v-for="(code, index) in user.regional_codes" :key="index" 
                                class="ma-1" color="#800080" variant="outlined">
                          {{ code }}
                        </v-chip>
                      </v-card>

                      <v-card class="pa-3 mt-2" variant="outlined">
                        <p class="font-weight-bold">Area Codes:</p>
                        <v-chip v-for="(code, index) in user.area_codes" :key="index" 
                                class="ma-1" color="#800080" variant="outlined">
                          {{ code }}
                        </v-chip>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Edit User Dialog - FIXED SCROLL -->
      <v-dialog v-model="edit_user_dialog" fullscreen id="app_user_list">
        <v-card flat class="d-flex flex-column" style="height: 100vh; overflow: hidden;">
          <v-toolbar style="border-radius: 0px !important; flex-shrink: 0;"
                    class="custom_fullscreen_dialog_toolbar pr-5 custom_card_box" color="white" dense>
            <v-toolbar-title>
              <v-img class="custom_company_logo_nav" :src="require('@/img/company_logo/logo-dark 2.png')"
                    @click="homeReload()"></v-img>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn style="background-color:#800080; color: white;" variant="flat" @click="edit_user_dialog = false">
              CLOSE
            </v-btn>
          </v-toolbar>

          <v-container fluid class="pa-0 flex-grow-1" style="overflow-y: auto; height: 0; min-height: 0;">
            <v-row class="ma-0" style="padding: 16px 0;">
              <v-col cols="12" md="12">
                <v-card class="pa-0 mx-6 mt-2 custom_card_box" border="sm opacity-1">
                  <template v-slot:prepend>
                    <v-icon>mdi-view-dashboard</v-icon>
                    <v-card-title class="font-weight-bold custom_page_main_sub_title ml-3">UPDATE USER</v-card-title>
                  </template>
                </v-card>

                <v-card class="pa-3 mx-6 custom_card_box mt-4 mb-4" border="sm opacity-1">
                  <v-row class="ma-0">
                    <v-col cols="12" xs="12" sm="9" md="9" lg="9">
                      <v-row>
                        <v-col cols="12" xs="12" sm="4" md="2" lg="2">
                          <div class="image card-h4-title" style="position: relative; display: inline-block;">
                            <img 
                              :src="brokenImages.has(user.id) ? defaultAvatar : getUserImageUrl(user)" 
                              class="user_image_view" 
                              style="width: 100px; height: 100px; object-fit: cover; border-radius: 8px;"
                              @error="handleImageError(user.id)"
                            />
                            <v-btn class="custom_user_image_upload_btn" icon rounded 
                                  size="small" style="background-color: #800080; color: white; position: absolute; bottom: -5px; right: -5px;" @click="dialog_image_upload=true">
                              <v-icon size="small">mdi-upload</v-icon>
                              <v-tooltip activator="parent" location="top">Upload User Image</v-tooltip>
                            </v-btn>
                          </div>
                        </v-col>
                        <v-col cols="12" xs="12" sm="8" md="10" lg="10">
                          <div class="d-flex">
                            <p class="font-weight-bold custom_page_component_main_title text-body-1">Full name: &nbsp;</p>
                            <p class="font-weight-bold custom_page_component_main_title"
                              style="color: #05146F !important;">{{ user.name }}</p>
                          </div>

                          <div class="d-flex mt-1">
                            <p class="font-weight-bold custom_page_component_main_title text-body-1">User Code: &nbsp;</p>
                            <p class="font-weight-medium text-body-2 custom_page_component_main_title mt-1">
                              {{ user.user_code }}
                            </p>
                          </div>

                          <div class="d-flex mt-1">
                            <p class="font-weight-bold custom_page_component_main_title text-body-1">Mobile: &nbsp;</p>
                            <p class="font-weight-medium text-body-2 custom_page_component_main_title mt-1">
                              {{ user.mobile_numbers ? user.mobile_numbers.map(num => num.country_code + num.mobile_number).join(', ') : '' }}
                            </p>
                          </div>

                          <div class="d-flex mt-6">
                            <v-chip class="text-body-1 mr-3 custom_green_chip" variant="outlined">
                              <v-icon color="#1B5E20">mdi-check-circle-outline</v-icon>&nbsp;
                              {{ user.user_category }} - &nbsp;<span style="text-transform: uppercase !important;">{{ user.user_type }}</span>
                            </v-chip>
                            <v-chip :color="user.status === 'Activate' ? 'green' : 'red'" variant="outlined">
                              {{ user.status === 'Activate' ? 'ACTIVE ✅' : 'INACTIVE ❌' }}
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>

                    <v-col cols="12" xs="12" sm="3" md="3" lg="3">
                      <v-row class="text-right mt-3 mr-3">
                        <v-spacer></v-spacer>
                        <v-btn style="background-color: #800080; color: white;" size="large" 
                              variant="flat" @click="updateUser">UPDATE USER
                        </v-btn>
                      </v-row>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="12" lg="12">
                      <v-divider></v-divider>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" xs="12" sm="12" md="6" lg="6">
                      <v-text-field 
                        v-model="user.name" 
                        class="px-3 custom_input_field_color" 
                        label="User Full Name *" 
                        required 
                        variant="outlined" 
                        density="comfortable"
                        :rules="[v => !!v || 'Full Name is required']"
                        @input="checkFullNameExists"
                        :error-messages="fieldErrors.fullName">
                      </v-text-field>

                      <v-text-field 
                        v-model="user.user_name" 
                        class="px-3 mt-2 custom_input_field_color"
                        label="User Name *" 
                        required 
                        variant="outlined" 
                        density="comfortable"
                        :rules="[v => !!v || 'User Name is required']"
                        @input="checkUserNameExists"
                        @blur="checkUserNameExists"
                        :error-messages="fieldErrors.userName">
                      </v-text-field>

                      <v-text-field 
                        v-model="user.user_code" 
                        class="px-3 mt-2 custom_input_field_color"
                        label="User Code *" 
                        required 
                        variant="outlined" 
                        density="comfortable"
                        :rules="[v => !!v || 'User Code is required']"
                        @input="checkUserCodeExists"
                        @blur="checkUserCodeExists"
                        :error-messages="fieldErrors.userCode">
                      </v-text-field>

                      <v-autocomplete 
                        v-model="user.user_category" 
                        :items="userCategoryItems"
                        class="px-3 mt-2 custom_input_field_color" 
                        label="User Category *"
                        placeholder="Select User Category"
                        required 
                        variant="outlined" 
                        density="comfortable"
                        :rules="[v => !!v || 'User Category is required']"
                        @update:model-value="onUserCategoryChange">
                      </v-autocomplete>

                      <v-autocomplete 
                        v-model="user.user_type" 
                        :items="filteredAssignableUserTypes"
                        :rules="[v => !!v || 'User role is required']"
                        class="px-3 mt-2 custom_input_field_color" 
                        clearable 
                        item-title="role_type" 
                        item-value="role_type" 
                        label="User Role Type *"
                        placeholder="Select User Type Here" 
                        required 
                        variant="outlined" 
                        density="comfortable"
                        :disabled="!user.user_category || loadingUserTypes"
                        :loading="loadingUserTypes">
                      </v-autocomplete>

                      <v-text-field 
                        v-model="user.email" 
                        class="px-3 mt-2 custom_input_field_color" 
                        label="Email Address *" 
                        placeholder="Type Email Here"
                        required 
                        variant="outlined" 
                        density="comfortable"
                        readonly
                        bg-color="#f5f5f5"
                        style="background-color: #f5f5f5; cursor: default; opacity: 0.8;">
                        <template v-slot:prepend-inner>
                          <v-icon size="small" color="grey">mdi-lock</v-icon>
                        </template>
                      </v-text-field>

                      <v-text-field 
                        v-model="user.registration_num" 
                        class="px-3 mt-2 custom_input_field_color"
                        label="Registration Number *" 
                        placeholder="Type Registration Number Here"
                        required 
                        variant="outlined" 
                        density="comfortable"
                        :rules="[v => !!v || 'Registration Number is required']"
                        @input="checkRegistrationNumExists"
                        @blur="checkRegistrationNumExists"
                        :error-messages="fieldErrors.registrationNum">
                      </v-text-field>

                      <v-text-field 
                        v-model="user.nic" 
                        :rules="nicRules" 
                        class="px-3 mt-2 custom_input_field_color"
                        label="NIC *" 
                        placeholder="Type NIC" 
                        required 
                        variant="outlined" 
                        density="comfortable"
                        @input="checkNICExists"
                        @blur="checkNICExists"
                        :error-messages="fieldErrors.nic">
                      </v-text-field>

                      <v-autocomplete v-model="user.gender" :items="gender" class="px-3 mt-2 custom_input_field_color"
                                    label="Gender *" placeholder="Select Gender" required variant="outlined" 
                                    density="comfortable">
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="6" lg="6">
                      <v-col cols="12" xs="12" sm="12" md="12" lg="12">
                        <v-row class="mb-4">
                          <v-col cols="12" xs="12" sm="12" md="12" lg="12">
                            <v-btn variant="flat" color="#F3E5F5" class="text-none"
                                  style="color: #800080; border: 1px solid #800080" @click="addPhoneNo"
                                  :disabled="user.mobile_numbers && user.mobile_numbers.length >= 2">
                              <v-icon start>mdi-plus-circle-outline</v-icon> ADD PHONE NUMBERS
                            </v-btn>
                            <v-divider class="mt-3"></v-divider>
                          </v-col>
                        </v-row>

                        <div v-for="(phoneNo, index) in user.mobile_numbers" :key="index" class="mb-3">
                          <v-row no-gutters align="center">
                            <v-col cols="2">
                              <div class="country-code-box d-flex align-center justify-center"
                                  style="border: 1px solid #B0B0B0; border-radius: 4px; background-color: #F5F5F5; height: 40px; font-size: 14px; padding: 0 8px;">
                                <span class="font-weight-medium">+94</span>
                              </div>
                            </v-col>
                            <v-col cols="7" class="px-2">
                              <v-text-field 
                                v-model="phoneNo.mobile_number" 
                                :label="index === 0 ? 'Primary Mobile Number *' : 'Secondary Mobile Number'"
                                :rules="getPhoneNumberRules(index)"
                                density="comfortable" 
                                class="custom_input_field_color"
                                :placeholder="index === 0 ? 'Type primary mobile number here' : 'Type secondary mobile number here'" 
                                variant="outlined" 
                                hide-details="auto"
                                @blur="validatePhoneNumber(index)">
                              </v-text-field>
                            </v-col>
                            <v-col cols="3">
                              <v-btn v-if="user.mobile_numbers && user.mobile_numbers.length > 1 && index > 0" 
                                    variant="flat" color="#FFEBEE" class="text-none w-100"
                                    style="color: #D32F2F; border: 1px solid #FFCDD2; height: 40px; font-size: 12px;" 
                                    @click="removePhoneNo(index)">
                                <v-icon start size="18">mdi-minus-circle-outline</v-icon> REMOVE
                              </v-btn>
                            </v-col>
                          </v-row>
                        </div>
                      </v-col>

                      <v-select v-model="user.piyestra_companies" :items="piyestraCompanies" multiple
                                class="px-3 mt-2 custom_input_field_color" label="Piyestra Companies *"
                                placeholder="Select Companies" variant="outlined" density="comfortable"
                                chips closable-chips :rules="[v => v && v.length > 0 || 'At least one company is required']">
                      </v-select>

                      <!-- Regional Code - Multi Select WITH ALL OPTION + CAPPED CHIPS -->
                      <v-select 
                        v-model="user.regional_codes" 
                        :items="regionalCodeItemsWithAll" 
                        label="Regional Code *" 
                        placeholder="Select regional code(s)"
                        variant="outlined" 
                        density="comfortable" 
                        class="px-3 mt-2" 
                        multiple 
                        :rules="[v => v && v.length > 0 || 'At least one Regional Code is required']"
                        :loading="loadingRegionalCodes"
                        @update:model-value="onRegionalCodesChange">
                        <template v-slot:item="{ item }">
                          <v-list-item
                            :title="item.raw.title"
                            :value="item.raw.value"
                            @click.stop="onRegionalDropdownItemClick(item.raw)">
                            <template v-slot:prepend>
                              <v-checkbox-btn
                                :model-value="isRegionalItemChecked(item.raw)"
                                @click.stop="onRegionalDropdownItemClick(item.raw)"
                              ></v-checkbox-btn>
                            </template>
                          </v-list-item>
                        </template>
                        <template v-slot:selection="{ index }">
                          <template v-if="index === 0">
                            <v-chip v-for="code in visibleRegionalChips.slice(0, maxVisibleChips)" :key="code"
                              size="small" closable class="ma-1" @click:close="removeRegionalChip(code)">
                              {{ code }}
                            </v-chip>
                            <span v-if="visibleRegionalChips.length > maxVisibleChips"
                              class="text-caption text-grey-darken-1 ml-2">
                              +{{ visibleRegionalChips.length - maxVisibleChips }} more
                            </span>
                          </template>
                        </template>
                      </v-select>

                      <!-- Area Code - Multi Select WITH ALL OPTION + CAPPED CHIPS -->
                      <v-select 
                        v-model="user.area_codes" 
                        :items="areaCodeItemsWithAll" 
                        label="Area Code *" 
                        placeholder="Select area code(s)"
                        variant="outlined" 
                        density="comfortable" 
                        class="px-3 mt-2" 
                        multiple 
                        :rules="[v => v && v.length > 0 || 'At least one Area Code is required']"
                        :disabled="!user.regional_codes || user.regional_codes.length === 0 || loadingAreaCodes"
                        :loading="loadingAreaCodes"
                        clearable
                        @update:model-value="onAreaCodesChange">
                        <template v-slot:item="{ item }">
                          <v-list-item
                            :title="item.raw.title"
                            :value="item.raw.value"
                            @click.stop="onAreaDropdownItemClick(item.raw)">
                            <template v-slot:prepend>
                              <v-checkbox-btn
                                :model-value="isAreaItemChecked(item.raw)"
                                @click.stop="onAreaDropdownItemClick(item.raw)"
                              ></v-checkbox-btn>
                            </template>
                          </v-list-item>
                        </template>
                        <template v-slot:selection="{ index }">
                          <template v-if="index === 0">
                            <v-chip v-for="code in visibleAreaChips.slice(0, maxVisibleChips)" :key="code"
                              size="small" closable class="ma-1" @click:close="removeAreaChip(code)">
                              {{ code }}
                            </v-chip>
                            <span v-if="visibleAreaChips.length > maxVisibleChips"
                              class="text-caption text-grey-darken-1 ml-2">
                              +{{ visibleAreaChips.length - maxVisibleChips }} more
                            </span>
                          </template>
                        </template>
                      </v-select>

                      <!-- Geofence Radio Buttons - Show for SALES REP or AREA MANAGER -->
                      <div v-if="isUserSalesRepOrAreaManager(user.user_type)" class="px-3 mt-2">
                        <label class="font-weight-medium mb-2" style="display: block; color: rgba(0,0,0,0.6);">Geofence *</label>
                        <div class="d-flex align-center" style="gap: 32px;">
                          <div class="d-flex align-center" style="cursor: pointer;" @click="geofenceEnabled = 1">
                            <v-radio 
                              v-model="geofenceEnabled" 
                              :value="1" 
                              color="#800080"
                              hide-details
                              density="comfortable">
                            </v-radio>
                            <span class="ml-2">Yes</span>
                          </div>
                          <div class="d-flex align-center" style="cursor: pointer;" @click="geofenceEnabled = 0">
                            <v-radio 
                              v-model="geofenceEnabled" 
                              :value="0" 
                              color="#800080"
                              hide-details
                              density="comfortable">
                            </v-radio>
                            <span class="ml-2">No</span>
                          </div>
                        </div>
                      </div>

                      <v-textarea v-model="user.address" 
                                class="px-3 mt-2 custom_input_field_color" 
                                label="Address *" 
                                placeholder="Type Address Here"
                                required 
                                rows="1" 
                                auto-grow
                                variant="outlined" 
                                density="comfortable">
                      </v-textarea>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete User confirmation dialog -->
      <v-dialog v-model="delete_user_dialog" id="app_user_list" max-height="500" max-width="500" persistent>
        <v-card id="app_user_list" rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">DELETE USER DATA</v-card-title>
              </template>
              <v-spacer></v-spacer>
              <template v-slot:append>
                <v-btn @click="delete_user_dialog = false" icon="mdi-close" size="x-small"></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>
                <div class="d-flex align-center mb-2">
                  <v-icon color="warning" size="small">mdi-alert</v-icon>
                  <span style="color: #05146F" class="text-body-1 font-weight-medium ml-2">
                    Do you want to delete this user?
                  </span>
                </div>

                <div style="opacity: 0.7;" class="text-body-2 mt-2 text-justify">
                  {{ 
                    UserAssociatedDataList.length > 0
                        ? "This user has associated data in the following tables. Deleting will permanently remove all associated data. Are you sure?"
                        : "This user will be permanently deleted immediately. This action cannot be undone."
                  }}
                </div>

                <v-divider class="mt-4"></v-divider>

                <v-timeline align="start" density="compact" class="mt-2" v-if="UserAssociatedDataList.length > 0">
                  <v-timeline-item v-for="(table, index) in UserAssociatedDataList" :key="index"
                                    dot-color="#800080" size="x-small">
                    <template v-slot:opposite></template>
                    <div class="text-body-2">Related area {{ table }}</div>
                  </v-timeline-item>
                </v-timeline>

                <v-divider class="mt-4"></v-divider>

                <div class="d-flex justify-end mt-4">
                  <v-btn style="background-color: #C80909; color: white;" @click="deleteItem"
                         :disabled="UserAssociatedDataList.length > 0">
                    DELETE
                  </v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" @click="delete_user_dialog = false">
                    CANCEL
                  </v-btn>
                </div>
              </v-card-item>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <!-- Deactivate/Activate User confirmation dialog -->
      <v-dialog v-model="user_status_dialog" id="app_user_list" max-height="500" max-width="500" persistent>
        <v-card id="app_user_list" rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                  {{ statusChangeAction === 'Deactivate' ? 'DEACTIVATE USER' : 'ACTIVATE USER' }}
                </v-card-title>
              </template>
              <v-spacer></v-spacer>
              <template v-slot:append>
                <v-btn @click="user_status_dialog = false" icon="mdi-close" size="x-small"></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>
                <v-card-title style="color: #05146F" class="text-body-1 font-weight-medium">
                  <v-icon color="warning" size="small">mdi-alert</v-icon>
                  &nbsp;&nbsp;Are you sure you want to {{ statusChangeAction.toLowerCase() }} this user?
                </v-card-title>

                <p style="opacity: 0.7;" class="text-body-2 mt-4 text-justify">
                  This user will be {{ statusChangeAction === 'Deactivate' ? 'deactivated' : 'activated' }} immediately.
                  Once {{ statusChangeAction === 'Deactivate' ? 'deactivated' : 'activated' }}, 
                  {{ statusChangeAction === 'Deactivate' ? 'they can no longer access the system' : 'they will be able to access the system again' }}.
                </p>

                <v-divider class="mt-4"></v-divider>

                <div class="float-end mt-4">
                  <v-btn style="background-color: #C80909; color: white;" @click="updateUserStatus">
                    {{ statusChangeAction === 'Deactivate' ? 'DEACTIVATE' : 'ACTIVATE' }}
                  </v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" @click="user_status_dialog = false">CANCEL</v-btn>
                </div>
              </v-card-item>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <!-- Upload user image dialog -->
      <v-dialog v-model="dialog_image_upload" id="app_user_list" max-height="600" max-width="500" persistent>
        <v-card id="app_user_list" rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">CHANGE USER IMAGE</v-card-title>
              </template>
              <v-spacer></v-spacer>
              <template v-slot:append>
                <v-btn @click="closeImageUploadDialog" icon="mdi-close" size="x-small"></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <div class="drop-area" @click="triggerFileInput" @drop.prevent="handleDrop" @dragover.prevent>
                <input ref="fileInput" accept="image/*" style="display: none;" type="file" @change="handleImageUpload"/>
                <div v-if="previewImage" class="image-card-container">
                  <v-card class="image-card">
                    <v-img :src="previewImage" class="image-preview"></v-img>
                    <v-btn class="remove-icon" icon size="x-small" @click.stop="removeImage">
                      <v-icon color="red" size="small">mdi-delete</v-icon>
                    </v-btn>
                  </v-card>
                </div>
                <div v-if="!previewImage" class="placeholder-container">
                  <v-icon size="36">mdi-cloud-upload</v-icon>
                  <span>Drag your photo here OR click to <span style="color:#800080;">Browse from device</span></span>
                </div>
              </div>

              <v-divider class="mt-4"></v-divider>

              <div class="float-end mt-4">
                <v-btn style="background-color: #800080; color: white;" @click="uploadImage">Upload</v-btn>
                <v-btn class="ml-3 custom_btn_secondary" @click="closeImageUploadDialog">CANCEL</v-btn>
              </div>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <!-- Reset Password Dialog - FIXED SCROLL -->
      <v-dialog v-model="reset_password_dialog" fullscreen id="app_user_list">
        <v-card flat class="d-flex flex-column" style="height: 100vh; overflow: hidden;">
          <v-toolbar style="border-radius: 0px !important; flex-shrink: 0;"
                     class="custom_fullscreen_dialog_toolbar pr-5 custom_card_box" color="white" dense>
            <v-toolbar-title>
              <v-img class="custom_company_logo_nav" :src="require('@/img/company_logo/logo-dark 2.png')"
                     @click="homeReload()"></v-img>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn style="background-color: #800080; color: white;" variant="flat" @click="reset_password_dialog = false">
              <v-icon>mdi-close-circle-outline</v-icon>&nbsp;&nbsp;CLOSE
            </v-btn>
          </v-toolbar>

          <v-container fluid class="pa-0 flex-grow-1" style="overflow-y: auto; height: 0; min-height: 0;">
            <v-row class="ma-0" style="padding: 16px 0;">
              <v-col cols="12" md="12">
                <v-card class="pa-0 mx-6 mt-2 custom_card_box" border="sm opacity-1">
                  <template v-slot:prepend>
                    <v-icon>mdi-view-dashboard</v-icon>
                    <v-card-title class="font-weight-bold custom_page_main_sub_title ml-3">RESET PASSWORD</v-card-title>
                  </template>
                </v-card>

                <v-card class="pa-3 mx-6 custom_card_box mt-4 mb-4" border="sm opacity-1">
                  <v-row class="ma-0">
                    <v-col cols="12" xs="12" sm="9" md="9" lg="9">
                      <v-row>
                        <v-col cols="12" xs="12" sm="3" md="2" lg="2">
                          <v-img 
                            :src="brokenImages.has(user.id) ? defaultAvatar : getUserImageUrl(user)" 
                            aspect-ratio="1" 
                            class="user_image_view" 
                            contain
                            @error="handleImageError(user.id)">
                          </v-img>
                        </v-col>
                        <v-col cols="12" xs="12" sm="9" md="10" lg="10">
                          <div class="d-flex">
                            <p class="font-weight-bold custom_page_component_main_title text-body-1">Full name: &nbsp;</p>
                            <p class="font-weight-bold custom_page_component_main_title"
                               style="color: #05146F !important;">{{ user.name }}</p>
                          </div>

                          <div class="d-flex mt-1">
                            <p class="font-weight-bold custom_page_component_main_title text-body-1">Mobile: &nbsp;</p>
                            <p class="font-weight-medium text-body-2 custom_page_component_main_title mt-1">
                              {{ user.mobile_numbers ? user.mobile_numbers.map(num => num.mobile_number).join(', ') : '' }}
                            </p>
                          </div>

                          <div class="d-flex mt-6">
                            <v-chip class="text-body-1 mr-3 custom_green_chip" variant="outlined">
                              <v-icon color="#1B5E20">mdi-check-circle-outline</v-icon>&nbsp;
                              USER TYPE - &nbsp;<span style="text-transform: uppercase !important;">{{ user.user_type }}</span>
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="12" lg="12">
                      <v-divider></v-divider>
                    </v-col>
                  </v-row>

                  <v-card-text>
                    <v-row>
                      <v-col cols="12" xs="12" sm="12" md="6" lg="6">
                        <v-tabs bg-color="#FFEBEE" v-model="tab" align-tabs="center"
                                next-icon="mdi-arrow-right-bold-box-outline" prev-icon="mdi-arrow-left-bold-box-outline"
                                show-arrows>
                          <v-tab class="custom_page_main_sub_title" value="one">GENERATE RESET LINK</v-tab>
                          <v-tab class="custom_page_main_sub_title" value="two">AUTO GENERATE PASSWORD</v-tab>
                          <v-tab class="custom_page_main_sub_title" value="three">MANUAL PASSWORD CHANGE</v-tab>
                        </v-tabs>

                        <v-tabs-window v-model="tab">
                          <v-tabs-window-item class="mt-9" value="one">
                            <v-btn class="text-body-2" style="background-color: #800080; color: white;" size="x-large" variant="flat"
                                   @click="generateResetLink(user)">
                              GENERATE & SEND RESET LINK
                            </v-btn>
                          </v-tabs-window-item>

                          <v-tabs-window-item class="mt-9" value="two">
                            <v-btn class="text-body-2" style="background-color: #800080; color: white;" size="x-large" variant="flat"
                                   @click="generateAndSendPassword(user)">
                              GENERATE PASSWORD & SEND
                            </v-btn>
                          </v-tabs-window-item>

                          <v-tabs-window-item class="mt-9 px-1" value="three">
                            <v-text-field v-model="manualPassword" 
                                          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                          :rules="[v => !!v || 'Password is required', v => (v && v.length >= 8) || 'Password must be at least 8 characters', v => /[A-Za-z]/.test(v) || 'Password must contain at least one letter', v => /\d/.test(v) || 'Password must contain at least one number']"
                                          :type="showPassword ? 'text' : 'password'" autocomplete="new-password"
                                          class="mt-2 custom_input_field_color" label="Password *" 
                                          placeholder="Type password here" required variant="outlined" 
                                          @click:append-inner="togglePasswordVisibility" density="comfortable">
                            </v-text-field>

                            <v-text-field v-model="manualConfirmPassword"
                                          :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                          :type="showConfirmPassword ? 'text' : 'password'" autocomplete="new-password"
                                          class="mt-2 custom_input_field_color" label="Confirm Password *"
                                          placeholder="Confirm your password here" required variant="outlined"
                                          @click:append-inner="toggleConfirmPasswordVisibility" density="comfortable">
                            </v-text-field>

                            <v-divider></v-divider>

                            <v-row class="pa-3 mb-2 mt-3">
                              <v-btn style="background-color: #800080; color: white;" size="large" variant="flat"
                                     @click="updatePasswordRest(user)">
                                UPDATE
                              </v-btn>
                              <v-btn class="ml-3" style="color: #800080;" size="large" variant="outlined"
                                     @click="reset_password_dialog = false">
                                CANCEL
                              </v-btn>
                            </v-row>
                          </v-tabs-window-item>
                        </v-tabs-window>
                      </v-col>

                      <v-col cols="12" xs="12" sm="12" md="6" lg="6"></v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackAlert.snackbar" :color="snackAlert.snackColor" 
                  location="top right" timeout="3000">
        {{ snackAlert.snackMessage }}
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from "xlsx";
import {getPermissions} from "@/plugins/permissions";

const DEFAULT_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlMGVlZjAiLz48Y2lyY2xlIGN4PSI1MCIgY3k9IjQwIiByPSIxNSIgZmlsbD0iIzkwYThhZCIvPjxwYXRoIGQ9Ik0yMCA3MCBMMjAgNjAgQzIwIDUwIDQwIDUwIDUwIDUwIEM2MCA1MCA4MCA1MCA4MCA2MCBMODAgNzAiIGZpbGw9IiM5MGE4YWQiLz48L3N2Zz4=';

export default {
  name: 'UserList',
  data() {
    return {
      defaultAvatar: DEFAULT_AVATAR,
      apiBaseUrl: process.env.VUE_APP_IMAGE_PUBLIC_URL || process.env.VUE_APP_API_BASE_URL,
      previewImage: null,
      imageBase64: null,
      brokenImages: new Set(),
      centerLoading: false,

      ALL_VALUE: 'ALL',
      maxVisibleChips: 4,

      maxUsers: 300,
      maxWebUsers: 100,
      maxMobileUsers: 200,
      
      selectedUserStatus: [],
      selectedUserTypes: [],
      selectedUserCategories: [],
      selectedCompanies: [],
      selectedRegionalCodes: [],
      selectedAreaCodes: [],
      
      userStatusItems: ['Activate', 'Deactivate'],
      userCategoryItems: ['Web User', 'Mobile User', 'Web User, Mobile User'],
      allCompanies: [],
      allRegionalCodes: [],
      allAreaCodes: [],
      
      userTypes: [],
      userTypeOptions: [],
      loadingUserTypes: false,
      
      piyestraCompanies: [
        'Piyestra Furniture (Pvt) Ltd',
        'D.R.Industries (Pvt) Ltd',
        'D.R.Home Appliances (Pvt) Ltd'
      ],
      
      areaCodeOptions: [],
      loadingRegionalCodes: false,
      loadingAreaCodes: false,
      
      search: '',
      headers: [
        {text: 'USER CODE', value: 'user_code', title: 'USER CODE', sortable: true},
        {text: 'USER NAME', value: 'user_name', title: 'USER NAME', sortable: true},
        {text: 'FULL NAME', value: 'name', title: 'FULL NAME', sortable: true},
        {text: 'USER ROLE', value: 'user_type', title: 'USER ROLE', sortable: true},
        {text: 'USER CATEGORY', value: 'user_category', title: 'USER CATEGORY', sortable: true},
        {text: 'COMPANY', value: 'piyestra_companies', title: 'COMPANY', sortable: false},
        {text: 'REGIONAL CODE', value: 'regional_codes', title: 'REGIONAL CODE', sortable: false},
        {text: 'MOBILE', value: 'mobile_numbers', title: 'MOBILE', sortable: true},
        {text: 'ACTION', value: 'action', title: 'ACTION'}
      ],
      items: [],
      
      phoneRules: [
        v => !!v || "Mobile Number is required",
        v => /^\d{9}$/.test(v) || "Must be 9 digits (without country code)"
      ],
      nicRules: [
        v => !!v || 'NIC is required',
        v => /^[0-9]{9}[vVxX]$|^[0-9]{12}$/.test(v) || 'Must be a valid NIC number'
      ],
      gender: ['Male', 'Female'],
      
      itemsPerPage: 10,
      page: 1,
      totalPages: 1,
      loading: false,
      
      delete_user_dialog: false,
      user_status_dialog: false,
      view_user_dialog: false,
      edit_user_dialog: false,
      reset_password_dialog: false,
      dashboardSelectDialog: false,
      dialog_image_upload: false,
      
      user: {
        mobile_numbers: [{country_code: '+94', mobile_number: ''}],
        piyestra_companies: [],
        regional_codes: [],
        area_codes: []
      },
      
      fieldErrors: {
        fullName: '',
        userName: '',
        userCode: '',
        registrationNum: '',
        nic: ''
      },
      
      showPassword: false,
      showConfirmPassword: false,
      tab: null,
      manualPassword: '',
      manualConfirmPassword: '',
      
      permissionsItems: [],
      permissions: {
        read: false,
        write: false,
        delete: false
      },
      premissionStatus: '',
      geofenceEnabled: 1,
      
      UserAssociatedDataList: [],
      deleteItemId: null,
      statusChangeUserId: null,
      statusChangeAction: '',
      
      snackAlert: {
        snackbar: false,
        snackColor: '',
        snackMessage: ''
      },
      
      phoneValidationErrors: []
    };
  },

  mounted() {
    this.refreshPermissions();
    const dashboardId = 3;
    this.permissions = getPermissions(dashboardId);
    this.fetchDataintoTable();
    this.getAllUserRoles();
  },

  computed: {
    // Only count ACTIVE users (status === 'Activate') for stat cards
    activeUsers() {
      return this.items.filter(item => item.status === 'Activate');
    },

    totalUsers() {
      return this.activeUsers.length;
    },
    
    webUsers() {
      return this.activeUsers.filter(item => 
        this.hasCategory(item.user_category, 'Web User')
      ).length;
    },
    
    mobileUsers() {
      return this.activeUsers.filter(item => 
        this.hasCategory(item.user_category, 'Mobile User')
      ).length;
    },
    
    bothUsers() {
      return this.activeUsers.filter(item => 
        this.hasCategory(item.user_category, 'Web User') && 
        this.hasCategory(item.user_category, 'Mobile User')
      ).length;
    },

    filteredItems() {
      const searchLower = this.search ? this.search.toLowerCase() : '';

      return this.items.filter(item => {
        const matchesStatus = this.selectedUserStatus.length === 0 ||
          this.selectedUserStatus.includes(item.status);
        
        const matchesUserType = this.selectedUserTypes.length === 0 ||
          this.selectedUserTypes.includes(item.user_type);
        
        const matchesCategory = this.selectedUserCategories.length === 0 ||
          this.matchesCategoryFilter(item.user_category, this.selectedUserCategories);
        
        const matchesCompany = this.selectedCompanies.length === 0 ||
          item.piyestra_companies?.some(company => this.selectedCompanies.includes(company));
        
        const matchesRegionalCode = this.selectedRegionalCodes.length === 0 ||
          item.regional_codes?.some(code => this.selectedRegionalCodes.includes(code));
        
        const matchesAreaCode = this.selectedAreaCodes.length === 0 ||
          item.area_codes?.some(code => this.selectedAreaCodes.includes(code));

        const matchesSearch = !this.search ||
          item.user_name?.toLowerCase().includes(searchLower) ||
          item.name?.toLowerCase().includes(searchLower) ||
          item.user_code?.toLowerCase().includes(searchLower) ||
          item.registration_num?.toLowerCase().includes(searchLower) ||
          item.email?.toLowerCase().includes(searchLower) ||
          item.nic?.toLowerCase().includes(searchLower) ||
          item.piyestra_companies?.some(c => c.toLowerCase().includes(searchLower)) ||
          item.regional_codes?.some(c => c.toLowerCase().includes(searchLower)) ||
          item.area_codes?.some(c => c.toLowerCase().includes(searchLower));

        return matchesStatus && matchesUserType && matchesCategory && 
               matchesCompany && matchesRegionalCode && matchesAreaCode && matchesSearch;
      });
    },

    filteredAssignableUserTypes() {
      if (!this.user.user_category || !this.userTypes.length) return [];
      
      return this.userTypes.filter(role => {
        if (this.isSuperAdminRole(role?.role_type)) return false;
        
        const category = role.category || '';
        if (this.user.user_category === 'Web User') return category === 'Web User';
        if (this.user.user_category === 'Mobile User') return category === 'Mobile User';
        if (this.user.user_category === 'Web User, Mobile User') return category === 'Web User, Mobile User';
        return false;
      });
    },

    // ===== Regional Code / Area Code "ALL" helpers =====

    regionalCodeItemsWithAll() {
      return [
        { title: 'ALL', value: this.ALL_VALUE },
        ...this.allRegionalCodes.map(code => ({ title: code, value: code }))
      ];
    },

    areaCodeItemsWithAll() {
      return [
        { title: 'ALL', value: this.ALL_VALUE },
        ...this.areaCodeOptions.map(code => ({ title: code, value: code }))
      ];
    },

    visibleRegionalChips() {
      return (this.user.regional_codes || []).filter(v => v !== this.ALL_VALUE);
    },

    visibleAreaChips() {
      return (this.user.area_codes || []).filter(v => v !== this.ALL_VALUE);
    }
  },
  
  created() {
    this.permissions = getPermissions(3, "User List");
    if (!this.permissions.read) {
      this.$router.push('/dashboard');
    }
  },

  methods: {
    // ===== Category matching helper =====
    // Splits "Web User,Mobile User" or "Web User, Mobile User" by comma
    // and does exact match after trimming, so both formats work correctly.
    // Used by stats cards (counts a both-user as both web AND mobile).
    hasCategory(userCategory, targetCategory) {
      if (!userCategory) return false;
      const categories = String(userCategory)
        .split(',')
        .map(c => c.trim());
      return categories.includes(targetCategory);
    },

    // ===== Normalize a category string =====
    // Splits by comma, trims, sorts alphabetically, rejoins with ", ".
    // "Web User,Mobile User"  -> "Mobile User, Web User"
    // "Web User, Mobile User" -> "Mobile User, Web User"
    // "Web User"              -> "Web User"
    normalizeCategory(str) {
      if (!str) return '';
      return String(str)
        .split(',')
        .map(c => c.trim())
        .filter(Boolean)
        .sort()
        .join(', ');
    },

    // ===== Category filter logic for the data table =====
    // Rules:
    //   - Web User                          -> only "Web User"
    //   - Mobile User                       -> only "Mobile User"
    //   - Web User, Mobile User             -> only "Web User, Mobile User"
    //   - Web User + Mobile User (both)     -> only individual "Web User" + "Mobile User",
    //                                          EXCLUDES "Web User, Mobile User"
    //   - Web User + Web User, Mobile User  -> shows both those groups
    //   - Mobile User + Web User, Mobile User -> shows both those groups
    //   - all three                         -> shows all three groups
    matchesCategoryFilter(userCategory, selectedCategories) {
      if (!selectedCategories || selectedCategories.length === 0) return true;
      if (!userCategory) return false;

      const itemCat = this.normalizeCategory(userCategory);
      const normalizedSelected = selectedCategories.map(c => this.normalizeCategory(c));

      const hasWeb = normalizedSelected.includes('Web User');
      const hasMobile = normalizedSelected.includes('Mobile User');
      // Sorted form of "Web User, Mobile User" is "Mobile User, Web User"
      const hasBoth = normalizedSelected.includes('Mobile User, Web User');

      // Special case: ONLY the two individual options are ticked.
      // Show only individual Web User and individual Mobile User,
      // exclude the both-group.
      if (hasWeb && hasMobile && !hasBoth && selectedCategories.length === 2) {
        return itemCat === 'Web User' || itemCat === 'Mobile User';
      }

      // Every other combination: exact match against any selected category.
      return normalizedSelected.includes(itemCat);
    },

    isUserSalesRepOrAreaManager(userType) {
      if (!userType) return false;
      const normalized = String(userType).trim().toUpperCase();
      return normalized === 'SALES REP' || normalized === 'SALES_REP' || normalized === 'SALESREP' ||
             normalized === 'AREA MANAGER' || normalized === 'AREA_MANAGER' || normalized === 'AREAMANAGER';
    },

    getUserImageUrl(user) {
      if (!user || !user.user_image) {
        return this.defaultAvatar;
      }

      const imagePath = user.user_image;

      if (imagePath.startsWith('data:image')) {
        return imagePath;
      }

      if (imagePath.startsWith('blob:')) {
        return imagePath;
      }

      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
      }

      let cleanPath = imagePath;
      cleanPath = cleanPath.replace(/^\/?public\//, '/');
      
      if (!cleanPath.startsWith('/')) {
        cleanPath = '/' + cleanPath;
      }

      const baseUrl = this.apiBaseUrl.replace(/\/+$/, '');
      const fullUrl = `${baseUrl}${cleanPath}`;
      const cacheBuster = `?t=${user.updated_at || Date.now()}`;
      
      return fullUrl + cacheBuster;
    },

    dataURLtoBlob(dataURL) {
      const arr = dataURL.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    },

    normalizeRoleName(roleName) {
      return String(roleName || '').trim().toLowerCase().replace(/\s+/g, ' ');
    },

    isSuperAdminRole(roleName) {
      const normalized = this.normalizeRoleName(roleName);
      return normalized === 'super admin' || normalized === 'superadmin';
    },

    fetchDataintoTable() {
      this.loading = true;
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem("token");

      axios.post("/api/createdUserList", {
        userId: userId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).then((response) => {
        this.items = response.data.data.map(item => {
          const mobileNumbers = item.mobile_numbers || [];
          
          // Normalize category to consistent format: sorted, comma+space separated
          // "Web User,Mobile User"  -> "Mobile User, Web User"
          // "Web User, Mobile User" -> "Mobile User, Web User"
          const normalizedCategory = item.user_category
            ? item.user_category.split(',').map(c => c.trim()).sort().join(', ')
            : item.user_category;
          
          return {
            ...item,
            user_category: normalizedCategory,
            mobile_numbers: mobileNumbers.map(m => ({
              country_code: m.country_code || '+94',
              mobile_number: m.mobile_number || ''
            })),
            piyestra_companies: item.piyestra_companies || [],
            regional_codes: item.regional_codes || [],
            area_codes: item.area_codes || [],
            user_image: item.user_image || null
          };
        }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        this.populateFilterOptions();
        this.loading = false;
      }).catch((error) => {
        console.error('Error fetching user list:', error);
        this.showSnackbar('Failed to load user list', 'error');
        this.loading = false;
      });
    },

    populateFilterOptions() {
      const companies = new Set();
      const regionalCodes = new Set();
      const areaCodes = new Set();
      
      this.items.forEach(item => {
        item.piyestra_companies?.forEach(company => companies.add(company));
        item.regional_codes?.forEach(code => regionalCodes.add(code));
        item.area_codes?.forEach(code => areaCodes.add(code));
      });
      
      this.allCompanies = Array.from(companies);
      this.allRegionalCodes = Array.from(regionalCodes);
      this.allAreaCodes = Array.from(areaCodes);
    },

    getAllUserRoles() {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');

      axios.post('/api/getUserRolesType', {
        userId: userId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        this.userTypes = response.data.data;
        this.userTypeOptions = response.data.data;
      })
      .catch(error => {
        console.error('Error fetching user roles:', error);
      });
    },

    fetchFilteredUserRoles() {
      if (!this.user.user_category) {
        return;
      }
      
      this.loadingUserTypes = true;
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      
      axios.post('/api/getUserRolesType', {
        userId: userId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then(response => {
        if (response.data && Array.isArray(response.data.data)) {
          this.userTypes = response.data.data;
          this.userTypeOptions = response.data.data;
        }
        this.loadingUserTypes = false;
      })
      .catch(error => {
        console.error('Error fetching user roles:', error);
        this.showSnackbar('Failed to load user roles', 'error');
        this.loadingUserTypes = false;
      });
    },

    async refreshPermissions() {
      const dashboardId = 3;
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      try {
        const response = await axios.get(`/api/getUserById/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.success && response.data.data) {
          const userData = response.data.data;
          
          if (userData.user_role) {
            localStorage.setItem('userRole', JSON.stringify(userData.user_role));
            
            if (userData.user_role.accessDashboard) {
              const permissionsMap = {};
              userData.user_role.accessDashboard.forEach(perm => {
                permissionsMap[perm.dashboard_id] = {
                  read: perm.read_premission === 1,
                  write: perm.write_premission === 1,
                  delete: perm.delete_premission === 1
                };
              });
              localStorage.setItem('userPermissions', JSON.stringify(permissionsMap));
            }
          }
        }
      } catch (error) {
        console.error('Error refreshing permissions:', error);
      }
      
      this.permissions = getPermissions(dashboardId);
    },

    async fetchRegionalCodes() {
      this.loadingRegionalCodes = true;
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem("token");

      try {
        const response = await axios.get(`/api/region-code-list`, {
          params: {
            userId: userId
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data?.success === true && Array.isArray(response.data.data)) {
          this.allRegionalCodes = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching regional codes:', error);
        this.showSnackbar('Failed to load regional codes', 'error');
      } finally {
        this.loadingRegionalCodes = false;
      }
    },

    async fetchAreaCodesForRegions(regionCodes) {
      if (!regionCodes || regionCodes.length === 0) {
        this.areaCodeOptions = [];
        return;
      }

      this.loadingAreaCodes = true;
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem("token");

      try {
        const allAreaCodes = new Set();
        
        const regionValues = regionCodes
          .filter(r => r !== this.ALL_VALUE)
          .map(region => {
            if (typeof region === 'object' && region.value) {
              return region.value;
            }
            return region;
          });

        if (regionValues.length === 0) {
          this.areaCodeOptions = [];
          this.loadingAreaCodes = false;
          return;
        }
        
        const fetchPromises = regionValues.map(regionCode => 
          axios.get(`/api/area-code-by-region`, {
            params: {
              userId: userId,
              region_code: regionCode
            },
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }).catch(error => {
            console.warn(`Failed to fetch area codes for region ${regionCode}:`, error);
            return { data: { data: [] } };
          })
        );

        const responses = await Promise.all(fetchPromises);
        
        responses.forEach(response => {
          if (response.data?.success === true && Array.isArray(response.data.data)) {
            response.data.data.forEach(code => allAreaCodes.add(code));
          }
        });

        this.areaCodeOptions = Array.from(allAreaCodes);
      } catch (error) {
        console.error('Error fetching area codes:', error);
        this.showSnackbar('Failed to load area codes', 'error');
      } finally {
        this.loadingAreaCodes = false;
      }
    },

    // ===== Regional Code / Area Code ALL handlers =====

    isRegionalItemChecked(item) {
      if (item.value === this.ALL_VALUE) {
        return (this.user.regional_codes || []).includes(this.ALL_VALUE);
      }
      return (this.user.regional_codes || []).includes(item.value);
    },

    isAreaItemChecked(item) {
      if (item.value === this.ALL_VALUE) {
        return (this.user.area_codes || []).includes(this.ALL_VALUE);
      }
      return (this.user.area_codes || []).includes(item.value);
    },

    onRegionalDropdownItemClick(item) {
      const allCodes = this.allRegionalCodes;
      const current = [...(this.user.regional_codes || [])];
      const allValue = this.ALL_VALUE;

      let result;

      if (item.value === allValue) {
        const allRealSelected = allCodes.length > 0 && allCodes.every(c => current.includes(c));
        result = allRealSelected ? [] : [allValue, ...allCodes];
      } else {
        const isChecked = current.includes(item.value);
        if (isChecked) {
          result = current.filter(v => v !== item.value && v !== allValue);
        } else {
          result = [...current.filter(v => v !== allValue), item.value];
          if (allCodes.length > 0 && allCodes.every(c => result.includes(c))) {
            result = [allValue, ...result];
          }
        }
      }

      this.user.regional_codes = result;

      const realRegions = result.filter(v => v !== allValue);
      this.user.area_codes = [];
      if (realRegions.length > 0) {
        this.fetchAreaCodesForRegions(realRegions);
      } else {
        this.areaCodeOptions = [];
      }
    },

    onAreaDropdownItemClick(item) {
      const allCodes = this.areaCodeOptions;
      const current = [...(this.user.area_codes || [])];
      const allValue = this.ALL_VALUE;

      let result;

      if (item.value === allValue) {
        const allRealSelected = allCodes.length > 0 && allCodes.every(c => current.includes(c));
        result = allRealSelected ? [] : [allValue, ...allCodes];
      } else {
        const isChecked = current.includes(item.value);
        if (isChecked) {
          result = current.filter(v => v !== item.value && v !== allValue);
        } else {
          result = [...current.filter(v => v !== allValue), item.value];
          if (allCodes.length > 0 && allCodes.every(c => result.includes(c))) {
            result = [allValue, ...result];
          }
        }
      }

      this.user.area_codes = result;
    },

    removeRegionalChip(value) {
      const allValue = this.ALL_VALUE;
      let result;

      if (value === allValue) {
        result = [];
      } else {
        result = (this.user.regional_codes || []).filter(v => v !== value);
        const realCount = result.filter(v => v !== allValue).length;
        if (result.includes(allValue) && realCount < this.allRegionalCodes.length) {
          result = result.filter(v => v !== allValue);
        }
      }

      this.user.regional_codes = result;

      const realRegions = result.filter(v => v !== allValue);
      this.user.area_codes = [];
      if (realRegions.length > 0) {
        this.fetchAreaCodesForRegions(realRegions);
      } else {
        this.areaCodeOptions = [];
      }
    },

    removeAreaChip(value) {
      const allValue = this.ALL_VALUE;
      let result;

      if (value === allValue) {
        result = [];
      } else {
        result = (this.user.area_codes || []).filter(v => v !== value);
        const realCount = result.filter(v => v !== allValue).length;
        if (result.includes(allValue) && realCount < this.areaCodeOptions.length) {
          result = result.filter(v => v !== allValue);
        }
      }

      this.user.area_codes = result;
    },

    onRegionalCodesChange(selectedRegions) {
      const currentlySelectedAreaCodes = this.user.area_codes ? [...this.user.area_codes] : [];
      
      if (selectedRegions && selectedRegions.length > 0) {
        this.fetchAreaCodesForRegions(selectedRegions).then(() => {
          if (currentlySelectedAreaCodes.length > 0) {
            const validAreaCodes = currentlySelectedAreaCodes.filter(selectedCode => 
              this.areaCodeOptions.includes(selectedCode)
            );
            
            if (JSON.stringify(validAreaCodes) !== JSON.stringify(this.user.area_codes)) {
              this.user.area_codes = validAreaCodes;
            }
            
            if (validAreaCodes.length < currentlySelectedAreaCodes.length) {
              this.showSnackbar('Some previously selected area codes are no longer available and have been removed', 'info');
            }
          }
        });
      } else {
        this.areaCodeOptions = [];
        this.user.area_codes = [];
      }
    },

    onAreaCodesChange() {
      // no extra logic — chip removals & item clicks handle everything
    },

    onUserCategoryChange() {
      this.user.user_type = '';
      this.fetchFilteredUserRoles();
    },

    checkFullNameExists() {
      if (!this.user.name || this.user.name.length < 2) {
        this.fieldErrors.fullName = '';
        return;
      }

      const exists = this.items.some(item =>
        item.id !== this.user.id &&
        item.name && item.name.toLowerCase().trim() === this.user.name.toLowerCase().trim()
      );

      this.fieldErrors.fullName = exists ? 'This full name already exists' : '';
    },

    checkUserNameExists() {
      if (!this.user.user_name || this.user.user_name.length < 3) {
        this.fieldErrors.userName = '';
        return;
      }

      const exists = this.items.some(item =>
        item.id !== this.user.id &&
        item.user_name && item.user_name.toLowerCase().trim() === this.user.user_name.toLowerCase().trim()
      );

      this.fieldErrors.userName = exists ? 'This user name already exists' : '';
    },

    checkUserCodeExists() {
      if (!this.user.user_code || this.user.user_code.length < 2) {
        this.fieldErrors.userCode = '';
        return;
      }

      const exists = this.items.some(item =>
        item.id !== this.user.id &&
        item.user_code && item.user_code.toLowerCase().trim() === this.user.user_code.toLowerCase().trim()
      );

      this.fieldErrors.userCode = exists ? 'This user code already exists' : '';
    },

    checkRegistrationNumExists() {
      if (!this.user.registration_num || this.user.registration_num.length < 2) {
        this.fieldErrors.registrationNum = '';
        return;
      }

      const exists = this.items.some(item =>
        item.id !== this.user.id &&
        item.registration_num && item.registration_num.toLowerCase().trim() === this.user.registration_num.toLowerCase().trim()
      );

      this.fieldErrors.registrationNum = exists ? 'This registration number already exists' : '';
    },

    checkNICExists() {
      if (!this.user.nic || this.user.nic.length < 2) {
        this.fieldErrors.nic = '';
        return;
      }

      const exists = this.items.some(item =>
        item.id !== this.user.id &&
        item.nic && item.nic.toUpperCase().trim() === this.user.nic.toUpperCase().trim()
      );

      this.fieldErrors.nic = exists ? 'This NIC already exists' : '';
    },

    async checkPhoneNumberExists(mobileNumber, currentUserId) {
      if (!mobileNumber || mobileNumber.length !== 9) {
        return false;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post('/api/check-phone-number-exists', {
          mobile_number: mobileNumber,
          country_code: '+94',
          exclude_user_id: currentUserId
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        return response.data.exists === true;
      } catch (error) {
        console.error('Error checking phone number:', error);
        return false;
      }
    },

    isPhoneNumberDuplicateInSameUser(phoneNumber, currentIndex) {
      const numbers = this.user.mobile_numbers || [];
      for (let i = 0; i < numbers.length; i++) {
        if (i !== currentIndex && numbers[i].mobile_number === phoneNumber) {
          return true;
        }
      }
      return false;
    },

    async validatePhoneNumber(index) {
      const phoneNumber = this.user.mobile_numbers[index]?.mobile_number;
      
      if (!phoneNumber) {
        return;
      }
      
      if (!/^\d{9}$/.test(phoneNumber)) {
        this.showSnackbar(`Mobile number ${phoneNumber} must be exactly 9 digits`, 'error');
        return;
      }
      
      if (this.isPhoneNumberDuplicateInSameUser(phoneNumber, index)) {
        this.showSnackbar(`Mobile number ${phoneNumber} is already added as another contact for this user`, 'error');
        this.user.mobile_numbers[index].mobile_number = '';
        return;
      }
      
      const currentUserId = this.user.id;
      const exists = await this.checkPhoneNumberExists(phoneNumber, currentUserId);
      
      if (exists) {
        this.showSnackbar(`Mobile number ${phoneNumber} is already registered to another user`, 'error');
        this.user.mobile_numbers[index].mobile_number = '';
      }
    },

    getPhoneNumberRules(index) {
      return [
        v => !!v || "Mobile Number is required",
        v => /^\d{9}$/.test(v) || "Must be 9 digits (without country code)",
        v => {
          if (v && this.isPhoneNumberDuplicateInSameUser(v, index)) {
            return "This phone number is already added for this user";
          }
          return true;
        }
      ];
    },

    async validateAllPhoneNumbers() {
      const numbers = this.user.mobile_numbers || [];
      
      for (let i = 0; i < numbers.length; i++) {
        const phoneNumber = numbers[i].mobile_number;
        
        if (!phoneNumber) {
          this.showSnackbar('Please fill in all phone numbers', 'error');
          return false;
        }
        
        if (!/^\d{9}$/.test(phoneNumber)) {
          this.showSnackbar(`Mobile number ${phoneNumber} must be exactly 9 digits`, 'error');
          return false;
        }
        
        if (this.isPhoneNumberDuplicateInSameUser(phoneNumber, i)) {
          this.showSnackbar(`Mobile number ${phoneNumber} appears multiple times for this user`, 'error');
          return false;
        }
        
        const currentUserId = this.user.id;
        const exists = await this.checkPhoneNumberExists(phoneNumber, currentUserId);
        
        if (exists) {
          this.showSnackbar(`Mobile number ${phoneNumber} is already registered to another user`, 'error');
          return false;
        }
      }
      
      return true;
    },

    editItem(item) {
      if (!this.permissions.write) {
        this.showSnackbar("You don't have permission to edit users", "error");
        return;
      }
      
      this.user = {
        ...item,
        mobile_numbers: item.mobile_numbers && item.mobile_numbers.length > 0 
          ? [...item.mobile_numbers] 
          : [{country_code: '+94', mobile_number: ''}],
        piyestra_companies: item.piyestra_companies && item.piyestra_companies.length > 0 
          ? [...item.piyestra_companies] 
          : [],
        regional_codes: item.regional_codes && item.regional_codes.length > 0 
          ? [...item.regional_codes] 
          : [],
        area_codes: item.area_codes && item.area_codes.length > 0 
          ? [...item.area_codes] 
          : []
      };
      
      this.fieldErrors = {
        fullName: '',
        userName: '',
        userCode: '',
        registrationNum: '',
        nic: ''
      };
      
      const geofenceValue = item.geofence_enabled;
      this.geofenceEnabled = geofenceValue === 1 || 
                            geofenceValue === "1" || 
                            geofenceValue === true || 
                            geofenceValue === "yes" || 
                            geofenceValue === "on" ? 1 : 0;
      
      if (this.user.user_category) {
        this.fetchFilteredUserRoles();
      }
      
      this.edit_user_dialog = true;
      
      this.fetchRegionalCodes().then(() => {
        if (this.user.regional_codes && this.user.regional_codes.length > 0) {
          this.fetchAreaCodesForRegions(this.user.regional_codes).then(() => {
            if (this.user.area_codes && this.user.area_codes.length > 0) {
              const validAreaCodes = this.user.area_codes.filter(code => 
                this.areaCodeOptions.includes(code)
              );
              
              if (validAreaCodes.length < this.user.area_codes.length) {
                this.user.area_codes = validAreaCodes;
                this.showSnackbar('Some area codes were no longer available and have been removed', 'info');
              }
            }
          });
        }
      });
    },

    viewItem(item) {
      this.user = { ...item };
      this.view_user_dialog = true;
    },

    lockItem(item) {
      if (!this.permissions.write) {
        this.showSnackbar("You don't have permission to change passwords", "error");
        return;
      }
      this.user = { ...item };
      this.reset_password_dialog = true;
    },

    async generateResetLink(user) {
      this.centerLoading = true;
      const emails = user.email;
      const baseUrl = window.location.origin;

      try {
        const response = await axios.post('/api/forgetPassword', {
          email: emails,
          baseUrl: baseUrl
        });
        if (response.status === 200) {
          this.showSnackbar("Reset Link successfully sent. Check user mail", "success");
          this.centerLoading = false;
          this.reset_password_dialog = false;
        } else {
          this.showSnackbar("Failed to send reset email. Please try again.", "error");
          this.centerLoading = false;
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || "Failed to send reset email.";
        this.showSnackbar(errorMsg, "error");
        this.centerLoading = false;
      }
    },

    generateAndSendPassword(user) {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token')?.trim();
      const email = user.email;
      this.centerLoading = true;

      if (!token) {
        console.error("No token found. Please log in again.");
        this.centerLoading = false;
        return;
      }

      axios.post(`${process.env.VUE_APP_API_BASE_URL}/api/autogenaratePassword`,
        {
          userId: userId,
          email: email,
          token: token
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        .then(response => {
          if (response.status === 200) {
            this.centerLoading = false;
            this.showSnackbar("Password generated and sent to user", "success");
            this.reset_password_dialog = false;
          } else {
            console.error("Error generating password:", response.statusText);
            this.centerLoading = false;
            this.showSnackbar("Failed to generate password", "error");
          }
        })
        .catch(error => {
          console.error("There was an error generating the password!", error);
          this.centerLoading = false;
          this.showSnackbar("Failed to generate password", "error");
        });
    },

    confirmStatusChange(item) {
      if (!this.permissions.write) {
        this.showSnackbar("You don't have permission to change user status", "error");
        return;
      }
      this.user_status_dialog = true;
      this.statusChangeUserId = item.id;
      this.statusChangeAction = item.status === 'Activate' ? 'Deactivate' : 'Activate';
    },

    updateUserStatus() {
      this.centerLoading = true;
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');

      axios.post('/api/updateUserStatus', {
        userId: userId,
        userListId: this.statusChangeUserId,
        action: this.statusChangeAction
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (response.data.success) {
          this.fetchDataintoTable();
          this.user_status_dialog = false;
          this.centerLoading = false;
          this.showSnackbar(`User ${this.statusChangeAction.toLowerCase()}d Successfully`, "success");
        } else {
          this.showSnackbar(response.data.message || `${this.statusChangeAction} Failed`, "error");
          this.user_status_dialog = false;
          this.centerLoading = false;
        }
      })
      .catch(error => {
        console.error('Error updating user status:', error);
        const errorMsg = error.response?.data?.message || `${this.statusChangeAction} Failed`;
        this.showSnackbar(errorMsg, "error");
        this.user_status_dialog = false;
        this.centerLoading = false;
      });
    },

    async confirmDelete(item) {
      if (!this.permissions.delete) {
        this.showSnackbar("You don't have permission to delete users", "error");
        return;
      }

      this.deleteItemId = item.id;
      this.UserAssociatedDataList = [];
      this.delete_user_dialog = true;
    },

    deleteItem() {
      this.centerLoading = true;

      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const userListId = this.deleteItemId;

      axios.delete(`${process.env.VUE_APP_API_BASE_URL}/api/forceDeleteUser/${userListId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: {
          userId: userId
        }
      })
      .then(() => {
        this.fetchDataintoTable();
        this.delete_user_dialog = false;
        this.edit_user_dialog = false;
        this.view_user_dialog = false;
        this.centerLoading = false;
        this.showSnackbar("The user was Successfully Deleted Permanently", "success");
      })
      .catch(error => {
        console.error('Error deleting users:', error);
        this.delete_user_dialog = false;
        this.edit_user_dialog = false;
        this.view_user_dialog = false;
        this.centerLoading = false;
        this.showSnackbar("Delete Failed", "error");
      });
    },

    async updateUser() {
      try {
          const token = localStorage.getItem("token");
          const userId = localStorage.getItem('userId');

          if (!this.user.id) {
              console.error("User ID is missing");
              return;
          }

          this.checkFullNameExists();
          this.checkUserNameExists();
          this.checkUserCodeExists();
          this.checkRegistrationNumExists();
          this.checkNICExists();

          if (this.fieldErrors.fullName || 
              this.fieldErrors.userName || 
              this.fieldErrors.userCode || 
              this.fieldErrors.registrationNum || 
              this.fieldErrors.nic) {
            this.showSnackbar('Please fix duplicate field errors before updating', 'error');
            return;
          }

          if (!this.user.user_type) {
              this.showSnackbar('User Type is required.', 'error');
              return;
          }

          if (this.isSuperAdminRole(this.user.user_type)) {
              this.showSnackbar('Super admin role cannot be assigned from user edit.', 'warning');
              return;
          }

          if (!this.user.piyestra_companies || this.user.piyestra_companies.length === 0) {
              this.showSnackbar('At least one Piyestra company is required.', 'error');
              return;
          }

          if (!this.user.regional_codes || this.user.regional_codes.length === 0) {
              this.showSnackbar('At least one Regional Code is required.', 'error');
              return;
          }

          if (!this.user.area_codes || this.user.area_codes.length === 0) {
              this.showSnackbar('At least one Area Code is required.', 'error');
              return;
          }

          const arePhoneNumbersValid = await this.validateAllPhoneNumbers();
          if (!arePhoneNumbersValid) {
              this.centerLoading = false;
              return;
          }

          this.centerLoading = true;

          const formData = new FormData();
          
          formData.append('fullName', this.user.name);
          formData.append('userName', this.user.user_name);
          formData.append('userCode', this.user.user_code);
          formData.append('emailAddress', this.user.email);
          formData.append('userType', this.getUserRoleId(this.user.user_type));
          formData.append('nic', this.user.nic);
          formData.append('gender', this.user.gender);
          formData.append('geofence', this.geofenceEnabled === 1 ? "yes" : "no");
          formData.append('userCategory', this.user.user_category);
          formData.append('address', this.user.address || '');
          formData.append('updatedBy', userId);
          
          if (this.user.registration_num) {
              formData.append('registrationNum', this.user.registration_num);
          }
          
          const mobileNumbersJson = JSON.stringify(
              this.user.mobile_numbers.map(phone => ({
                  countryCode: phone.country_code || '+94',
                  number: phone.mobile_number
              }))
          );
          formData.append('mobileNumbers', mobileNumbersJson);
          formData.append('piyestraCompanies', JSON.stringify(this.user.piyestra_companies));
          formData.append('regionalCodes', JSON.stringify(this.user.regional_codes.filter(c => c !== this.ALL_VALUE)));
          formData.append('areaCodes', JSON.stringify(this.user.area_codes.filter(c => c !== this.ALL_VALUE)));
          
          if (this.previewImage && this.previewImage !== this.getUserImageUrl(this.user)) {
            if (this.previewImage.startsWith('data:image')) {
              const blob = this.dataURLtoBlob(this.previewImage);
              const file = new File([blob], `user_${this.user.id}_${Date.now()}.jpg`, { type: 'image/jpeg' });
              formData.append('userImage', file);
            } else if (this.previewImage instanceof File) {
              formData.append('userImage', this.previewImage);
            }
          }

          const response = await axios.post(
              `/api/updateUser/${this.user.id}`,
              formData,
              {
                  headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'multipart/form-data'
                  }
              }
          );

          if (response.status === 200 || response.status === 201) {
              this.showSnackbar("User updated successfully", "success");
              await this.refreshUserData(this.user.id);
              
              this.edit_user_dialog = false;
              this.view_user_dialog = false;
              
              this.previewImage = null;
              this.imageBase64 = null;
              this.fetchDataintoTable();
              
              setTimeout(() => {
                  this.$router.push('/users-list');
              }, 1500);
          } else {
              this.showSnackbar(response.data.message || "Failed to update user", "error");
          }
      } catch (error) {
          console.error("Error updating user:", error);
          if (error.response) {
              const errorMessage = error.response.data?.message || 
                                  error.response.data?.error || 
                                  "Failed to update user";
              this.showSnackbar(errorMessage, "error");
          } else if (error.request) {
              this.showSnackbar("No response from server. Please check your connection.", "error");
          } else {
              this.showSnackbar("An error occurred while updating user", "error");
          }
      } finally {
          this.centerLoading = false;
      }
    },

    async refreshUserData(userId) {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(`/api/getUserById/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.success && response.data.data) {
          const updatedUser = response.data.data;
          
          const index = this.items.findIndex(item => item.id === userId);
          if (index !== -1) {
            this.items[index] = {
              ...this.items[index],
              ...updatedUser,
              user_image: updatedUser.user_image
            };
          }
          
          if (this.user.id === userId) {
            this.user.user_image = updatedUser.user_image;
          }
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    },

    clearImageCache(userId) {
      this.brokenImages.delete(userId);
      setTimeout(() => {
        this.$forceUpdate();
      }, 500);
    },

    getUserRoleId(roleType) {
      const role = this.userTypes.find(r => r.role_type === roleType);
      return role ? role.id : roleType;
    },

    addPhoneNo() {
      if (!this.user.mobile_numbers) {
        this.user.mobile_numbers = [];
      }
      if (this.user.mobile_numbers.length < 2) {
        this.user.mobile_numbers.push({country_code: '+94', mobile_number: ''});
      } else {
        this.showSnackbar("Maximum 2 phone numbers allowed", "warning");
      }
    },

    removePhoneNo(index) {
      if (this.user.mobile_numbers && this.user.mobile_numbers.length > 1) {
        this.user.mobile_numbers.splice(index, 1);
      }
    },

    updatePasswordRest(user) {
      if (!this.validateForm()) {
        return;
      }

      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token')?.trim();
      const email = user.email;
      this.centerLoading = true;

      if (!token) {
        console.error("No token found. Please log in again.");
        this.centerLoading = false;
        return;
      }

      axios.post(`${process.env.VUE_APP_API_BASE_URL}/api/manualEnterPassword`,
        {
          userId: userId,
          email: email,
          password: this.manualPassword,
          confirmPassword: this.manualConfirmPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })
        .then(response => {
          if (response.status === 200) {
            this.showSnackbar("Password updated successfully.", 'success');
            this.manualPassword = '';
            this.manualConfirmPassword = '';
            this.centerLoading = false;
            this.reset_password_dialog = false;
          } else {
            console.error("Error generating password:", response.statusText);
            this.centerLoading = false;
            this.showSnackbar("Password Updated failed", "error");
          }
        })
        .catch(error => {
          console.error("There was an error generating the password!", error);
          this.centerLoading = false;
          this.showSnackbar(error.response?.data?.error || "An error occurred.", 'error');
        });
    },

    validateForm() {
      if (!this.manualPassword || this.manualPassword.length < 8) {
        this.showSnackbar("Password must be at least 8 characters long.", "error");
        return false;
      }
      if (!/[A-Za-z]/.test(this.manualPassword)) {
        this.showSnackbar("Password must contain at least one letter.", "error");
        return false;
      }
      if (!/\d/.test(this.manualPassword)) {
        this.showSnackbar("Password must contain at least one number.", "error");
        return false;
      }
      if (this.manualPassword !== this.manualConfirmPassword) {
        this.showSnackbar("Passwords do not match.", "error");
        return false;
      }
      return true;
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewImage = e.target.result;
          const fullBase64 = e.target.result;
          this.imageBase64 = fullBase64.split(',')[1];
        };
        reader.readAsDataURL(file);
      }
    },

    handleImageError(userId) {
      console.warn(`Image failed to load for user ${userId}`);
      this.brokenImages.add(userId);
    },

    handleDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file) {
        this.handleImageUpload({target: {files: [file]}});
      }
    },

    removeImage() {
      this.previewImage = null;
      this.imageBase64 = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    },

    uploadImage() {
      if (this.previewImage) {
        this.dialog_image_upload = false;
        this.showSnackbar("Image selected successfully. It will be saved when you update the user.", "success");
      } else {
        this.showSnackbar("No image to upload", "error");
      }
    },

    closeImageUploadDialog() {
      this.dialog_image_upload = false;
      this.previewImage = null;
      this.imageBase64 = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    },

    handleSwitchChange(item) {
      if (!item.isOn) {
        item.permissions.read = false;
        item.permissions.write = false;
        item.permissions.delete = false;
      }
    },

    handlePermissionChange() {
      // Optional: Add logic for permission dependencies
    },

    selectAll() {
      this.permissionsItems.forEach((item) => {
        item.isOn = true;
        item.permissions.read = true;
        item.permissions.write = true;
        item.permissions.delete = true;
      });
    },

    cancelAll() {
      this.permissionsItems.forEach((item) => {
        item.isOn = false;
        item.permissions.read = false;
        item.permissions.write = false;
        item.permissions.delete = false;
      });
    },

    dashboardSelectDialogClose() {
      this.dashboardSelectDialog = false;
      setTimeout(() => {
        this.premissionStatus = null;
      }, 1000);
    },

    exportExcel() {
      const worksheet = XLSX.utils.json_to_sheet(
        this.filteredItems.map(item => ({
          'User Code': item.user_code,
          'User Name': item.user_name,
          'Full Name': item.name,
          'User Role': item.user_type,
          'Category': item.user_category,
          'Email': item.email,
          'NIC': item.nic,
          'Gender': item.gender,
          'Registration No': item.registration_num,
          'Mobile': item.mobile_numbers?.map(m => `${m.country_code} ${m.mobile_number}`).join(', '),
          'Companies': item.piyestra_companies?.join(', '),
          'Regional Codes': item.regional_codes?.join(', '),
          'Area Codes': item.area_codes?.join(', '),
          'Status': item.status
        }))
      );
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
      XLSX.writeFile(workbook, 'User_List.xlsx');
      
      this.logDocumentDownload('User_List.xlsx', 'excel');
    },

    exportPDF() {
      const doc = new jsPDF();
      
      doc.setFontSize(16);
      doc.text('User List Report', 14, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

      const tableColumn = [
        'User Code', 'User Name', 'Full Name', 'Role', 'Category', 'Status'
      ];
      
      const tableRows = this.filteredItems.map(item => [
        item.user_code,
        item.user_name,
        item.name,
        item.user_type,
        item.user_category,
        item.status
      ]);

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 40,
        theme: 'grid',
        headStyles: {
          fillColor: [128, 0, 128],
          textColor: [255, 255, 255],
          fontSize: 8
        },
        bodyStyles: {
          fontSize: 7
        }
      });

      doc.save('User_List.pdf');
      this.logDocumentDownload('User_List.pdf', 'pdf');
    },

    logDocumentDownload(fileName, fileType) {
      console.log(`Downloaded: ${fileName} (${fileType})`);
    },

    showSnackbar(message, color) {
      this.snackAlert.snackbar = true;
      this.snackAlert.snackMessage = message;
      this.snackAlert.snackColor = color;
    },

    backPageRoute() {
      this.$router.back();
    },

    createUser() {
      this.$router.push({path: '/user-registration'});
    },

    homeReload() {
      window.location.href = '/dashboard';
    }
  }
};
</script>

<style lang="scss" scoped>
@use "../../styles/user_management/users_list.scss" as *;

.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: default;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important;
  }
  
  .stat-label {
    font-size: 13px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .stat-number {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1.2;
    margin-top: 4px;
  }
  
  .stat-icon {
    opacity: 0.7;
  }
  
  .stat-progress {
    min-width: 100px;
    
    .stat-subtext {
      font-size: 11px;
      color: rgba(0, 0, 0, 0.5);
      margin-top: 2px;
    }
  }
  
  .stat-percentage {
    font-size: 14px;
    font-weight: 600;
    
    .green--text {
      color: #2E7D32;
    }
    
    .grey--text {
      color: rgba(0, 0, 0, 0.4);
    }
  }
}

.total-users {
  border-left: 4px solid #800080;
  
  .stat-progress {
    :deep(.v-progress-linear__background) {
      background-color: rgba(128, 0, 128, 0.15);
    }
  }
}

.web-users {
  border-left: 4px solid #1976D2;
  
  .stat-progress {
    :deep(.v-progress-linear__background) {
      background-color: rgba(25, 118, 210, 0.15);
    }
  }
}

.mobile-users {
  border-left: 4px solid #2E7D32;
  
  .stat-progress {
    :deep(.v-progress-linear__background) {
      background-color: rgba(46, 125, 50, 0.15);
    }
  }
}

.both-users {
  border-left: 4px solid #E65100;
}

.user_image_view {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
  background-color: #f5f5f5;
  display: block;
}

.v-img.user_image_view {
  background-color: #f5f5f5;
  
  .v-img__img {
    object-fit: contain;
  }
}

.drop-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #800080;
    background-color: #f9f0ff;
  }
}

.image-card {
  position: relative;
  display: inline-block;
  
  .image-preview {
    width: 200px;
    height: 150px;
    object-fit: cover;
  }
  
  .remove-icon {
    position: absolute;
    top: 5px;
    right: 5px;
    background-color: rgba(255, 255, 255, 0.8);
  }
}

.placeholder-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.country-code-box {
  font-size: 16px;
  font-weight: 500;
}

.fullscreen-dialog-content {
  height: calc(100vh - 64px);
  overflow-y: auto;
  overflow-x: hidden;
}
</style>